import type { ConversationDetail } from '@deskworks/shared-types';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useMemo } from 'react';
import { isTalkAsked } from '@/components/chat/desk-talk';
import { isOpenInChat, useOpenWorkItems, workItemsOfChat } from '@/components/chat/work-item-cards';
import { api } from '@/lib/api';
import { t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';
import { cn } from '@/lib/utils';

type Pulse = 'thinking' | 'you' | 'desks' | 'listening';

const PULSE: Record<Pulse, { label: string; className: string }> = {
  thinking: { label: t('Thinking along'), className: 'border-indigo-300 text-indigo-700 dark:border-indigo-800 dark:text-indigo-300' },
  you: { label: t('Waits for you'), className: 'border-orange-300 text-orange-700 dark:border-orange-800 dark:text-orange-300' },
  desks: { label: t('Desks at work'), className: 'border-indigo-200 text-indigo-700 dark:border-indigo-900 dark:text-indigo-300' },
  listening: { label: t('Listening'), className: 'text-muted-foreground' },
};

/**
 * Where a chat with VICKI stands, in one word next to its title: she thinks, something waits for a person (a Work Item,
 * one of her cards, a mission), the desks work on what she handed out or was asked, or she listens. It reads the queries
 * the chat already holds.
 */
function useVickiPulse(conv: ConversationDetail): Pulse {
  const messages = conv.messages;
  const has = useMemo(
    () => ({
      asked: messages.some(isTalkAsked),
      cards: messages.some((m) => (m.metadata as { event?: string } | undefined)?.event === 'vicki_action_requested'),
    }),
    [messages],
  );
  const openIds = useMemo(() => [...workItemsOfChat(messages).values()].filter(isOpenInChat).map((i) => i.id), [messages]);
  const items = useOpenWorkItems(openIds);
  const questions = useQuery({ queryKey: qk.chatQuestions(conv.id), queryFn: () => api.talk.ofChat(conv.id), enabled: has.asked }).data ?? [];
  const cards = useQuery({ queryKey: qk.vickiActions(conv.id), queryFn: () => api.frontDesk.actions(conv.id), enabled: has.cards }).data ?? [];
  const missions = useQuery({ queryKey: qk.vickiDrive(conv.id), queryFn: () => api.frontDesk.drive(conv.id) }).data?.missions ?? [];
  if (conv.agentState === 'starting' || conv.agentState === 'thinking') return 'thinking';
  if (
    items.some((i) => i.phase.tone === 'attention' || i.phase.tone === 'blocked') ||
    cards.some((a) => a.status === 'pending_approval') ||
    missions.some((m) => m.status === 'open' && m.waitingOn === 'people')
  ) {
    return 'you';
  }
  if (items.length || questions.some((q) => q.status === 'open') || missions.some((m) => m.status === 'open' && m.waitingOn === 'desks')) return 'desks';
  return 'listening';
}

/** VICKI's state in a chat as a chip (docs/features/front-desk.md#following-the-work). */
export function VickiStatusChip({ conv, className }: { conv: ConversationDetail; className?: string }) {
  const pulse = useVickiPulse(conv);
  const { label, className: tone } = PULSE[pulse];
  return (
    <span aria-live="polite" className={cn('inline-flex h-6 shrink-0 items-center gap-1.5 rounded-full border px-2 font-mono text-[11px] whitespace-nowrap', tone, className)}>
      {pulse === 'thinking' ? (
        <Loader2 className="size-3 animate-spin" />
      ) : (
        <span className={cn('size-1.5 rounded-full bg-current', pulse !== 'listening' && 'animate-soft-pulse')} />
      )}
      {label}
    </span>
  );
}
